/**
 * A form answered from a collection: the cells, the evidence behind one, and the chat that
 * argues with both.
 *
 * The three cell states stay three states all the way to the screen. An answered cell shows
 * its sentence, an unanswered one shows `— blank —` above the reason it is blank, and a
 * contradicted one shows what disagreed. Flattening the last two into an empty cell would
 * make a gap look like an oversight, which is the one thing a reviewer must never have to
 * guess about.
 *
 * Chat and evidence share the right pane deliberately: they are the same act — select a
 * cell, see what it rests on, argue with it — and separating them would put the evidence
 * out of sight of the thing questioning it.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { get, post } from "../lib/api";
import { usePermission } from "../hooks/useSession";
import type { FillCell, FillDetailResponse, FillMessage } from "../lib/types";
import { Shell } from "../components/Shell";
import { Button, Chip, Failed, Panel } from "../components/ui";

type Address = { section: string; row: string; column: string };

const STATE_TONE = {
  answered: "ok",
  not_found: "mut",
  conflicted: "crit",
} as const;

const STATE_LABEL = {
  answered: "answered",
  not_found: "not found",
  conflicted: "conflicted",
} as const;

function addressOf(cell: Address): string {
  return `${cell.section}/${cell.row}/${cell.column}`;
}

/** The counts a reviewer reads before anything else. */
function Measures({ detail }: { detail: FillDetailResponse }) {
  const measures: [string, number | string, string][] = [
    ["Cells", detail.answerable, "answerable"],
    ["Answered", detail.answered, "all cited"],
    ["Not found", detail.not_found, "nothing in the collection"],
    ["Conflicted", detail.conflicted, "documents disagree"],
  ];
  return (
    <div className="grid shrink-0 grid-cols-2 gap-px border-b border-hair bg-hair lg:grid-cols-4">
      {measures.map(([label, value, note]) => (
        <div key={label} className="flex items-baseline gap-2.5 bg-surface px-3.5 py-2.5">
          <span className="eyebrow shrink-0">{label}</span>
          <span
            className={`tnum font-mono text-[17px] leading-none font-bold ${
              label === "Conflicted" && Number(value) > 0 ? "text-crit" : ""
            }`}
          >
            {value}
          </span>
          <span className="truncate text-[11px] text-faint">{note}</span>
        </div>
      ))}
    </div>
  );
}

export function FillDetail() {
  const { id = "" } = useParams();
  const client = useQueryClient();
  const can = usePermission();
  const canCorrect = can("rows:correct");
  const [selected, setSelected] = useState<Address | null>(null);
  const [message, setMessage] = useState("");

  const detail = useQuery({
    queryKey: ["fills", id],
    queryFn: () => get<FillDetailResponse>(`/v1/fills/${id}`),
    // While a drafting pass runs the cells are landing section by section, so the screen
    // follows it rather than making someone reload to find out whether it finished.
    refetchInterval: (query) =>
      query.state.data?.fill.status === "drafting" ? 3000 : false,
  });
  const transcript = useQuery({
    queryKey: ["fills", id, "messages"],
    queryFn: () => get<{ messages: FillMessage[] }>(`/v1/fills/${id}/messages`),
  });

  const draft = useMutation({
    mutationFn: () => post<{ job_id: string }>(`/v1/fills/${id}/draft`, {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["fills", id] });
      void client.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
  const send = useMutation({
    mutationFn: () => post<{ reply: string; touched: string[] }>(`/v1/fills/${id}/messages`, {
      body: message,
    }),
    onSuccess: () => {
      setMessage("");
      void client.invalidateQueries({ queryKey: ["fills", id] });
    },
  });

  const cells = detail.data?.cells ?? [];
  const byAddress = useMemo(() => {
    const map = new Map<string, FillCell>();
    for (const cell of cells) map.set(addressOf(cell), cell);
    return map;
  }, [cells]);

  const structure = detail.data?.structure ?? undefined;
  const answerColumns = (structure?.columns ?? []).filter((column) => column.answerable !== false);
  const selectedCell = selected ? byAddress.get(addressOf(selected)) : undefined;
  const status = detail.data?.fill.status;

  return (
    <Shell
      fill
      crumb={
        <>
          <a href="/templates" className="text-dim hover:text-gold">
            templates
          </a>
          <span className="text-faint"> / </span>
          <span className="text-text">{detail.data?.fill.title ?? "fill"}</span>
        </>
      }
    >
      {detail.isError ? (
        <Failed error={detail.error} what="this fill" />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-hair bg-hair-soft px-4 py-2">
            {status === "drafting" ? (
              <Chip tone="gold" dot>
                drafting
              </Chip>
            ) : status === "failed" ? (
              <Chip tone="crit" dot>
                failed
              </Chip>
            ) : status === "drafted" ? (
              <Chip tone="ok" dot>
                drafted
              </Chip>
            ) : (
              <Chip tone="mut">not drafted</Chip>
            )}
            {detail.data?.fill.error && (
              <span className="text-[12px] text-crit">{detail.data.fill.error}</span>
            )}
            <div className="ml-auto flex items-center gap-2">
              {canCorrect && (
                <Button onClick={() => draft.mutate()} disabled={status === "drafting" || draft.isPending}>
                  {status === "draft" ? "draft" : "re-draft"}
                </Button>
              )}
              <a
                href={`/v1/fills/${id}/export?format=pdf`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-gold-surface bg-gold-surface px-3 py-1.5 font-mono text-[11px] font-bold tracking-[0.14em] text-on-gold uppercase hover:bg-gold-soft"
              >
                export pdf
              </a>
              <a
                href={`/v1/fills/${id}/export?format=docx`}
                className="inline-flex items-center gap-2 border border-hair-strong px-3 py-1.5 font-mono text-[11px] font-bold tracking-[0.14em] uppercase hover:border-gold hover:text-gold"
              >
                docx
              </a>
              <a
                href={`/v1/fills/${id}/export?format=xlsx`}
                className="inline-flex items-center gap-2 border border-hair-strong px-3 py-1.5 font-mono text-[11px] font-bold tracking-[0.14em] uppercase hover:border-gold hover:text-gold"
              >
                xlsx
              </a>
            </div>
          </div>

          {detail.data && <Measures detail={detail.data} />}

          <div className="grid min-h-0 flex-1 grid-cols-1 gap-px bg-hair lg:grid-cols-[minmax(0,1fr)_372px]">
            <Panel fill title="form">
              <div className="min-h-0 flex-1 overflow-auto">
                {structure === undefined ? (
                  <p className="p-4 text-[12.5px] text-dim">
                    Loading the form's structure…
                  </p>
                ) : (
                  structure.sections.map((section) => (
                    <div key={section.key}>
                      <div className="sticky top-0 z-2 flex items-center gap-3 border-b border-hair bg-raised px-3.5 py-2.5">
                        <span className="ordinal">{section.key}</span>
                        <h4 className="text-[12.5px] font-bold">{section.title}</h4>
                      </div>
                      {section.preamble && (
                        <p className="border-b border-hair-soft bg-ground-soft px-3.5 py-2.5 text-[12px] text-dim">
                          {section.preamble}
                        </p>
                      )}
                      <table className="w-full table-fixed border-collapse">
                        <thead>
                          <tr>
                            <th className="w-[46px] border-b border-hair px-3.5 py-1.5 text-left font-mono text-[10px] font-bold tracking-[0.18em] text-faint uppercase">
                              srl
                            </th>
                            <th className="w-[34%] border-b border-hair px-3.5 py-1.5 text-left font-mono text-[10px] font-bold tracking-[0.18em] text-faint uppercase">
                              requirement
                            </th>
                            {answerColumns.map((column) => (
                              <th
                                key={column.key}
                                className="border-b border-hair px-3.5 py-1.5 text-left font-mono text-[10px] font-bold tracking-[0.18em] text-faint uppercase"
                              >
                                {column.label}
                              </th>
                            ))}
                            <th className="w-[124px] border-b border-hair px-3.5 py-1.5 text-left font-mono text-[10px] font-bold tracking-[0.18em] text-faint uppercase">
                              state
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {section.rows.map((row) => {
                            const primary = answerColumns[0];
                            const address = primary
                              ? { section: section.key, row: row.key, column: primary.key }
                              : null;
                            const cell = address ? byAddress.get(addressOf(address)) : undefined;
                            const isSelected =
                              selected !== null && address !== null &&
                              addressOf(selected) === addressOf(address);
                            return (
                              <tr
                                key={row.key}
                                onClick={() => address && setSelected(address)}
                                className={`cursor-pointer ${isSelected ? "bg-gold-wash" : "hover:bg-hair-soft"}`}
                              >
                                <td className="border-b border-hair-soft px-3.5 py-2.5 align-top font-mono text-[11.5px] text-faint">
                                  ({row.key})
                                </td>
                                <td className="border-b border-hair-soft px-3.5 py-2.5 align-top text-[13px] text-dim">
                                  {row.requirement}
                                </td>
                                {answerColumns.map((column) => {
                                  const columnCell = byAddress.get(
                                    addressOf({ section: section.key, row: row.key, column: column.key }),
                                  );
                                  return (
                                    <td
                                      key={column.key}
                                      className="border-b border-hair-soft px-3.5 py-2.5 align-top text-[13px]"
                                    >
                                      {row.answerable === false ? null : columnCell?.state ===
                                        "answered" ? (
                                        <span>{columnCell.text}</span>
                                      ) : (
                                        <div className="flex flex-col gap-1.5">
                                          <span className="font-mono text-[11px] tracking-[0.1em] text-faint">
                                            — blank —
                                          </span>
                                          {columnCell?.reason && (
                                            <span className="text-[12px] text-faint italic">
                                              {columnCell.reason}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  );
                                })}
                                <td className="border-b border-hair-soft px-3.5 py-2.5 align-top">
                                  {row.answerable === false ? null : cell ? (
                                    <Chip tone={STATE_TONE[cell.state]}>
                                      {STATE_LABEL[cell.state]}
                                    </Chip>
                                  ) : (
                                    <Chip tone="mut">not drafted</Chip>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))
                )}
              </div>
            </Panel>

            <Panel fill title={selected ? addressOf(selected) : "evidence"}>
              {selectedCell && selectedCell.citations.length > 0 && (
                <div className="shrink-0 border-b border-hair bg-ground-soft p-3.5">
                  <span className="eyebrow">// what the document says</span>
                  {selectedCell.citations.map((citation, index) => (
                    <div key={`${citation.document_id}-${index}`} className="mt-2.5">
                      <blockquote className="border-l-2 border-gold-surface bg-page-bg p-3 text-[12.5px] leading-relaxed text-page-ink">
                        {citation.quote ?? "(no quote recorded)"}
                      </blockquote>
                      <a
                        href={`/documents/${citation.document_id}`}
                        className="mt-1.5 block font-mono text-[10px] tracking-[0.1em] text-faint hover:text-gold"
                      >
                        {citation.document_id.slice(0, 8)}
                        {citation.chunk_id ? ` · ${citation.chunk_id}` : ""}
                      </a>
                    </div>
                  ))}
                </div>
              )}

              <div className="min-h-0 flex-1 overflow-auto p-3.5">
                <div className="flex flex-col gap-3.5">
                  {(transcript.data?.messages ?? []).map((entry) => (
                    <div key={entry.id} className="flex flex-col gap-1.5">
                      <span
                        className={`font-mono text-[10px] tracking-[0.18em] uppercase ${
                          entry.role === "user" ? "text-gold" : "text-faint"
                        }`}
                      >
                        {entry.role === "user" ? "you" : "draft"}
                      </span>
                      <p
                        className={`text-[13px] leading-relaxed ${
                          entry.role === "user" ? "text-text" : "text-dim"
                        }`}
                      >
                        {entry.body}
                      </p>
                      {entry.touched.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {entry.touched.map((address) => (
                            <span
                              key={address}
                              className="bg-hair-soft px-1.5 py-0.5 font-mono text-[10px] text-dim"
                            >
                              {address}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {send.isPending && (
                    <span className="font-mono text-[10px] tracking-[0.18em] text-faint uppercase">
                      thinking…
                    </span>
                  )}
                  {send.isError && <Failed error={send.error} what="that turn" />}
                </div>
              </div>

              {canCorrect && (
                <form
                  className="shrink-0 border-t border-hair bg-ground-soft p-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (message.trim() !== "") send.mutate();
                  }}
                >
                  <div className="flex items-center gap-2.5 border border-hair-strong bg-surface px-2.5 py-2 focus-within:border-gold">
                    <span className="font-mono text-[12px] text-gold">&gt;</span>
                    <input
                      aria-label="ask about these documents"
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      placeholder="Ask about these documents, or say what to change"
                      className="flex-1 bg-transparent text-[13px] outline-none"
                    />
                  </div>
                </form>
              )}
            </Panel>
          </div>
        </div>
      )}
    </Shell>
  );
}
